import React, { useState } from 'react'
import CenterModal from '../../../components/Modals/center-modal';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as HomeIcon } from '../../../assets/icons/Home.svg';
import { ReactComponent as TokenIcon } from '../../../assets/plus_icon/token.svg';
import { ReactComponent as BookingIcon } from '../../../assets/plus_icon/booking.svg';
import { ReactComponent as SiteVisitIcon } from '../../../assets/plus_icon/sv_completed.svg';
import PinbarIcon from '../../../assets/plus_icon/pin_bar.png';
import { ReactComponent as UserIcon } from '../../../assets/plus_icon/user_icon.svg';
import { ReactComponent as ValidityIcon } from '../../../assets/plus_icon/cp_tagging.svg';
import moment from 'moment';
import AreYouSureModal from '../../../components/Modals/AreYouSureModal';

const CustomerRevisitModal = ({ 
    show, 
    closeModal, 
    customerData, 
    note, 
    setNote, 
    handleSendOTP, 
    setChannelPartner, 
    isWithCP,
    setIsWithCP,
    setCompanyId,
    setBrokerName,
    setBrokerMobile,
    setCompanyName,
    setIsChangeTagging,
    setExistingTagging,
    setRera,
    isCancelInternalVisit,
    setIsCancelInternalVisit
}) => {
    const [confirmPopup, setConfirmPopup] = useState({
        show: false,
        title: 'Are you Sure?',
        subText: '',
        onConfirm: null
    });
    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('DD-MMM-YYYY');
    };

    // Helper function to format budget range
    const formatBudget = (minBudget, maxBudget) => {
        if (!minBudget && !maxBudget) return '';
        const min = minBudget ? (parseInt(minBudget) / 100000).toFixed(1) + 'L' : '';
        const max = maxBudget ? (parseInt(maxBudget) / 100000).toFixed(1) + 'L' : '';
        return min && max ? `${min}-${max}` : min || max;
    };

    // Map API response to component data
    const mapCustomerData = (apiData) => {
        if (!apiData) return null;

        const isV2 = 'latest_completed_visit' in apiData || 
                     'latest_scheduled_visit' in apiData || 
                     'lead_info' in apiData;

        let mainVisit = {};
        let leadInfo = {};
        let latestCompleted = null;
        let latestScheduled = null;

        if (isV2) {
            latestCompleted = apiData.latest_completed_visit;
            latestScheduled = apiData.latest_scheduled_visit;
            leadInfo = apiData.lead_info;
            mainVisit = latestCompleted || latestScheduled || {};
        } else {
            // Backward compatibility for old flat API structure
            mainVisit = apiData;
            leadInfo = apiData;
        }

        const prefConfig = mainVisit.preferred_configurations || mainVisit.configuration;
        const configurationStr = Array.isArray(prefConfig) ? prefConfig[0] : (prefConfig || '');

        return {
            name: mainVisit.name || leadInfo?.name || '',
            phone: mainVisit.customer_mobile || leadInfo?.customer_mobile || '',
            email: mainVisit.customer_email || leadInfo?.customer_email || '',
            configuration: configurationStr || '',
            budget: formatBudget(mainVisit.min_budget, mainVisit.max_budget) || mainVisit.budget || '',
            leadSource: (leadInfo?.broker_user_id ? leadInfo.broker_company_name : (leadInfo?.last_tag_cp_firm_name || '')) || mainVisit.cp_company_name || '',
            cp_user_name: (leadInfo?.broker_user_id ? leadInfo.broker_user_name : (leadInfo?.last_tag_cp_user_name || '')) || mainVisit.cp_user_name || '',
            cp_company_name: (leadInfo?.broker_user_id ? leadInfo.broker_company_name : (leadInfo?.last_tag_cp_firm_name || '')) || mainVisit.cp_company_name || '',
            purpose: mainVisit.purpose || '',
            presales: leadInfo?.presale_user_name || mainVisit.presale_user_name || '',
            closing_user_name: leadInfo?.closing_user_name || mainVisit.closing_user_name || '',
            sourcing: leadInfo?.sourcing_user_name || mainVisit.sourcing_user_name || '',
            lastVisitDate: formatDate(latestCompleted ? latestCompleted.last_visit : (latestScheduled ? latestScheduled.schedule_date : mainVisit.last_visit)),
            siteVisitCount: latestCompleted?.sv_count || mainVisit.sv_count || 0,
            lastNote: mainVisit.last_note || '',
            noteAuthor: leadInfo?.presale_user_name || mainVisit.presale_user_name || 'System',
            noteCreatedAt: mainVisit.last_visit || '',
            status: latestCompleted ? 'SV Completed' : (latestScheduled?.status_name || leadInfo?.status_name || 'New Lead'),
            possession: mainVisit.possession || '',
            source: mainVisit.sub_source || '',
            tag_expiry: leadInfo?.tag_expiry || mainVisit.tag_expiry || ''
        };
    };

    const data = mapCustomerData(customerData);
    const isV2Data = customerData && ('latest_completed_visit' in customerData || 
                      'latest_scheduled_visit' in customerData || 
                      'lead_info' in customerData);
    const leadInfo = isV2Data ? customerData?.lead_info : customerData;
    const latestCompleted = isV2Data ? customerData?.latest_completed_visit : customerData;
    const latestScheduled = isV2Data ? customerData?.latest_scheduled_visit : customerData;
    const hasCompletedVisit = isV2Data ? (latestCompleted !== null && latestCompleted !== undefined) : (data?.siteVisitCount > 0);
    const isCase1 = !!(leadInfo?.broker_user_id && leadInfo?.broker_company_id && leadInfo?.tag_expiry && moment().isAfter(moment(leadInfo.tag_expiry)));
    const isCase2 = !!(!leadInfo?.broker_user_id && !leadInfo?.broker_company_id && leadInfo?.last_tag_cp_user);
    const isTagExpired = isCase1 || isCase2;
    const cpManagerName = isCase2
        ? (leadInfo?.last_tag_cp_user_name || '')
        : (leadInfo?.broker_user_name || data?.cp_user_name || latestCompleted?.cp_user_name || latestScheduled?.cp_user_name || '');

    const getInitials = (name) => {
        if (!name) return 'N/A';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    const isValidManagerName = (name) => {
        return (
            name && 
            typeof name === 'string' && 
            name.trim() !== '' && 
            name.toLowerCase() !== 'null' && 
            name.toLowerCase() !== 'undefined'
        );
    };

    const activeManagersCount = data ? [data.presales, data.closing_user_name, data.sourcing]
        .filter(name => isValidManagerName(name)).length : 0;

    // Don't render if no customer data
    if (!data) {
        return null;
    }

    const getStatusData = () => {
        const status = data.status || 'New Lead';
        
        let textColor = '#344054';
        let bgColor = '#F9FAFB';
        let borderColor = '#EAECF0';
        let Icon = SiteVisitIcon; // fallback icon

        if (status === 'Booking Completed') {
            textColor = '#D92D20'; // Red
            bgColor = '#FEF3F2';
            borderColor = '#FECDCA';
            Icon = BookingIcon;
        } else if (status === 'EOI Completed') {
            textColor = '#B54708'; // Amber
            bgColor = '#FFFAEB';
            borderColor = '#FEDF89';
            Icon = TokenIcon;
        } else if (status === 'SV Completed') {
            textColor = '#027A48'; // Green
            bgColor = '#ECFDF3';
            borderColor = '#D1FADF';
            Icon = SiteVisitIcon;
        } else if (status === 'SV Scheduled' || status === 'In Progress' || status === 'Final Negotiation') {
            textColor = '#175CD3'; // Blue
            bgColor = '#EFF8FF';
            borderColor = '#B2DDFF';
            Icon = SiteVisitIcon;
        } else if (status === 'SV Cancelled' || status === 'Dead Lead') {
            textColor = '#D92D20'; // Red
            bgColor = '#FEF3F2';
            borderColor = '#FECDCA';
            Icon = SiteVisitIcon;
        } else if (status === 'Not Connected') {
            textColor = '#B54708'; // Amber
            bgColor = '#FFFAEB';
            borderColor = '#FEDF89';
            Icon = SiteVisitIcon;
        }

        return (
            <div 
                style={{ 
                    color: textColor, 
                    backgroundColor: bgColor, 
                    borderColor: borderColor,
                    fontSize: '13px',
                    paddingLeft: '22px',
                    paddingRight: '22px'
                }}
                className='flex items-center gap-1 sm:gap-2 py-1.5 rounded-full font-semibold w-max border'
            >
                <Icon className='w-4 h-4' style={{ color: textColor }} />
                {status}
            </div>
        );
    };

    return (
        <CenterModal show={show} closeModal={closeModal} modalClass='!w-[760px]'>
            <div className=''>
                <style>{`
                    .revisit-confirm-backdrop {
                        background-color: #000000 !important;
                        opacity: 0.7 !important;
                        z-index: 1050 !important;
                    }
                    .modal:has(.revisit-confirm-modal) {
                        z-index: 1060 !important;
                    }
                    .revisit-confirm-modal .modal-content {
                        border-radius: 12px !important;
                    }
                    .revisit-confirm-modal .outline-btn,
                    .revisit-confirm-modal .pr-btn {
                        border-radius: 12px !important;
                    }
                    .team-avatar {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        flex-shrink: 0;
                    }
                    .team-member-card {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        transition: all 0.2s ease;
                        min-width: 0;
                    }
                    .team-member-card:hover .team-avatar {
                        transform: translateY(-3px) scale(1.08);
                        box-shadow: 0 4px 12px rgba(32, 100, 250, 0.18);
                    }
                    .truncate-text {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        max-width: 100%;
                    }
                `}</style>
{/* Header */}
<div className='flex items-center justify-between !px-9 !pt-9 !pb-8'>
    <h2 className='text-2xl font-bold text-gray-900 mb-0 flex items-center gap-3'>
        {hasCompletedVisit ? (
            <>
                Customer Revisit
                <span className='text-lg font-medium text-[#667085]'>
                    ({data.siteVisitCount || 0} Revisits)
                </span>
            </>
        ) : (
            "Customer Details"
        )}
    </h2>
 
    <button
        onClick={closeModal}
        className='p-1 hover:bg-gray-100 rounded'
    >
        <CloseIcon className='w-6 h-6' />
    </button>
</div>
 
{/* Customer Information Section */}
<div className='h-[65vh] overflow-y-auto !px-9'>
    <div className='border border-gray-200 !rounded-2xl !p-5 bg-white shadow-sm'>
        <div className='!mb-4'>
 
            {/* Top Section */}
            <div className='flex items-start justify-between mb-4'>
                <div className='flex items-start gap-3'>
                    <div className='w-12 h-12 bg-[#E5EFFF] rounded-lg flex items-center justify-center text-[#2064FA] font-semibold text-lg'>
                        {getInitials(data.name)}
                    </div>
 
                    <div>
                        <h3 className='text-lg font-semibold text-gray-900 mb-0'>
                            {data.name}
                        </h3>
 
                        <p className='text-gray-600 mb-0'>
                            {data.phone}
                        </p>
                    </div>
                </div>
 
                <div className='flex flex-col items-end gap-2'>
                    {data.lastVisitDate && (
                        <p className='text-xs font-bold text-gray-700 mb-0'>
                            Last Action : {data.lastVisitDate}
                        </p>
                    )}
                    {getStatusData()}
                </div>
            </div>
 
            {/* Channel Partner Details */}
            {data?.cp_company_name && (
                <div className='border border-gray-200 !rounded-xl !p-4 bg-[#FCFCFD] !mb-5'>
                    <div className='flex items-center'>
                        <UserIcon className='!mr-4' />
 
                        <div>
                            <h3 className='text-lg font-semibold text-gray-900 mb-0'>
                                {data.cp_user_name}
                            </h3>
 
                            <p className='text-gray-600 mb-0'>
                                {data.cp_company_name}
                            </p>
                        </div>
                    </div>
 
                    {data?.tag_expiry && (
                        <div className='flex tagging-validity-strip-bg !mt-3'>
                            <ValidityIcon />
 
                            <span>
                                Tagging valid till {moment(data?.tag_expiry).format('Do MMM YYYY')}
                            </span>
                        </div>
                    )}

                    {isValidManagerName(data?.presales) && (
                        <div className='flex items-center gap-2 !mt-3 text-[#2064FA] font-medium text-sm'>
                            <span className='w-2 h-2 rounded-full bg-[#2064FA] flex-shrink-0' />
                            <span>Customer already assigned to Pre-sale: <span className='font-bold'>{data.presales}</span></span>
                        </div>
                    )}
                </div>
            )}
 
            {/* Fill Customer Details Button */}
            {(!data?.purpose || !data?.budget || !data?.configuration || !data?.possession) &&
                <div className='mb-4 w-full'>
                    <button
                        className='w-full flex items-center justify-center bg-[#E5EFFF] !border !border-[#2064FA] !rounded-lg !px-4 !py-2 text-[#2064FA] font-medium hover:bg-[#D1E3FF] transition-colors'
                        onClick={() => {
                            closeModal();
                            setChannelPartner('3');
                        }}
                    >
                        <div className='w-6 h-6 bg-[#EEAA42] rounded-full flex items-center justify-center mr-3'>
                            <span className='text-white text-sm font-bold'>!</span>
                        </div>
 
                        Fill Customer Details
                    </button>
                </div>
            }
        </div>
 
        <div className='border-b border-gray-200 !my-6' />
 
        {/* Team Information Section */}
        {(isValidManagerName(data?.presales) ||
            isValidManagerName(data?.closing_user_name) ||
            isValidManagerName(data?.sourcing)) && (
                <div className='!mb-4 border border-gray-200 !py-4 !px-6 !rounded-xl'>
                    <div className={`grid gap-6 w-full ${
                        activeManagersCount === 3 
                            ? 'grid-cols-3' 
                            : activeManagersCount === 2 
                                ? 'grid-cols-2' 
                                : 'grid-cols-1'
                    }`}>
                        {isValidManagerName(data?.presales) && (
                            <div className='flex items-center gap-3 team-member-card min-w-0'>
                                <div className='w-12 h-12 rounded-full bg-[#E5EFFF] text-[#2064FA] font-bold text-lg flex items-center justify-center team-avatar'>
                                    {getInitials(data.presales)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p 
                                        title={data?.presales} 
                                        className='font-semibold text-gray-900 !mb-0 text-xl truncate-text'
                                    >
                                        {data?.presales}
                                    </p>
                                    <p className='text-gray-600 !mb-0 text-lg'>
                                        Presales
                                    </p>
                                </div>
                            </div>
                        )}

                        {isValidManagerName(data?.closing_user_name) && (
                            <div className='flex items-center gap-3 team-member-card min-w-0'>
                                <div className='w-12 h-12 rounded-full bg-[#ECFDF5] text-[#059669] font-bold text-lg flex items-center justify-center team-avatar'>
                                    {getInitials(data.closing_user_name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p 
                                        title={data?.closing_user_name} 
                                        className='font-semibold text-gray-900 !mb-0 text-xl truncate-text'
                                    >
                                        {data?.closing_user_name}
                                    </p>
                                    <p className='text-gray-600 !mb-0 text-lg'>
                                        Closing
                                    </p>
                                </div>
                            </div>
                        )}

                        {isValidManagerName(data?.sourcing) && (
                            <div className='flex items-center gap-3 team-member-card min-w-0'>
                                <div className='w-12 h-12 rounded-full bg-[#FFFBEB] text-[#D97706] font-bold text-lg flex items-center justify-center team-avatar'>
                                    {getInitials(data.sourcing)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p 
                                        title={data?.sourcing} 
                                        className='font-semibold text-gray-900 !mb-0 text-xl truncate-text'
                                    >
                                        {data?.sourcing}
                                    </p>
                                    <p className='text-gray-600 !mb-0 text-lg'>
                                        Sourcing
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
    </div>
    {/* Basic Details Tags */}
    <div className='!mt-6'>
    <p className='text-[#667085] text-sm font-semibold !mb-3'>
        Basic Details
        </p>
            <div className='flex flex-wrap gap-2'>
                {[
                    data.email,
                    data.configuration,
                    data.budget,
                    data.leadSource,
                    data.purpose,
                    data.possession,
                    data.source
                ]
                    .filter(tag => tag && tag.trim() !== '')
                    .map((tag, index) => (
                        <span
                            key={index}
                            className='bg-[#F1F1F599] text-[#344054] !px-3 !py-1.5 !rounded-lg text-sm font-medium'
                        >
                            {tag}
                        </span>
                    ))}
            </div>
         </div>
 
    {/* Last Site Visit Section */}
    <div className='!my-5'>
        {data.lastNote && (
            <>
                <h4 className='font-semibold text-[#314155] text-lg mb-2'>
                    Last Note
                </h4>
 
                <div className='bg-[#FCFCFD] border border-gray-200 rounded-xl p-4 mb-2 shadow-sm'>
                    <p className='text-gray-700 text-sm leading-relaxed'>
                        {data.lastNote}
                    </p>
 
                    {data.noteCreatedAt && (
                        <p className='text-gray-500 text-xs mt-3 text-right mb-0'>
                            <span className='font-medium text-[#314056]'>
                                {data.noteAuthor}
                            </span>
 
                            {" "}
                            (Created {moment(data.noteCreatedAt).format('h:mm A, Do MMM YYYY')})
                        </p>
                    )}
                </div>
            </>
        )}
    </div>
 
    {/* Note Section */}
    <div className='mb-6'>
        <div className='flex items-center mb-2'>
            <img
                src={PinbarIcon}
                alt="Pinbar"
                className='w-4 h-4 mr-1'
            />
 
            <h4 className='font-semibold text-gray-900 text-xl mb-0'>
                Note
            </h4>
        </div>
 
        <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='Add a Note...'
            className='w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            rows={3}
        />
    </div>
</div>

    {/* Footer Buttons */}
    <div className='bg-white border-t border-gray-200 !px-9 !py-4 flex gap-4 justify-between'>
    {/* 
    <button
        className='flex-1 border border-gray-300 rounded-xl py-3 px-4 font-medium text-gray-700 hover:bg-gray-50 transition-colors'
        onClick={() => {
            closeModal();
            setChannelPartner('3');
        }}
    >
        Edit Customer Details
    </button>
    */}

    {/* Button 1 (Secondary/Outline style): Continue Visit with CP */}
    {/* Button 1 & 2: Expired CP vs Standard CP check-in flows */}
    {isTagExpired ? (
        <>
            <button
                className='flex-1 border border-gray-300 rounded-xl py-3 px-4 font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                onClick={() => {
                    setConfirmPopup({
                        show: true,
                        title: 'Are you Sure?',
                        subText: isWithCP 
                            ? "Do you want to change tagging for this customer?" 
                            : "Do you want to remove tagging for this customer?",
                        onConfirm: () => {
                            if (setIsChangeTagging) setIsChangeTagging(true);
                            if (setExistingTagging) setExistingTagging('change');
                            if (setIsCancelInternalVisit) setIsCancelInternalVisit(false);
                            closeModal();
                            
                            // Clear any parent CP state
                            if (setBrokerName) setBrokerName('');
                            if (setBrokerMobile) setBrokerMobile('');
                            if (setCompanyName) setCompanyName('');
                            if (setCompanyId) setCompanyId(null);

                            if (isWithCP) {
                                if (setIsWithCP) setIsWithCP(true);
                                // Redirect to CP selection screen (BrokerDetailForm)
                                if (setChannelPartner) setChannelPartner('4');
                            } else {
                                if (setIsWithCP) setIsWithCP(false);
                                // Proceed directly to Direct OTP verification page
                                handleSendOTP(false);
                            }
                        }
                    });
                }}
            >
                {isWithCP ? "Change Tagging" : "Remove Tagging"}
            </button>
            <button
                className='flex-1 bg-primary text-white rounded-xl py-3 px-4 font-medium hover:opacity-90 transition-opacity'
                onClick={() => {
                    setConfirmPopup({
                        show: true,
                        title: 'Are you Sure?',
                        subText: "Are you sure you want to continue with same CP?",
                        onConfirm: () => {
                            if (setIsChangeTagging) setIsChangeTagging(true);
                            if (setExistingTagging) setExistingTagging('revive');
                            if (setIsCancelInternalVisit) setIsCancelInternalVisit(false);
                            closeModal();
                            if (setIsWithCP) setIsWithCP(true);

                            // Populate parent states with existing/last CP info
                            const isCase2 = !leadInfo?.broker_user_id && !leadInfo?.broker_company_id && leadInfo?.last_tag_cp_user;
                            const cpUserName = isCase2 
                                ? (leadInfo?.last_tag_cp_user_name || '') 
                                : (leadInfo?.broker_user_name || latestCompleted?.cp_user_name || latestScheduled?.cp_user_name || '');
                            const cpCompanyName = isCase2 
                                ? (leadInfo?.last_tag_cp_firm_name || '') 
                                : (leadInfo?.broker_company_name || latestCompleted?.cp_company_name || latestScheduled?.cp_company_name || '');
                            const brokerCompanyId = isCase2 
                                ? (leadInfo?.last_tag_cp_firm || null) 
                                : (leadInfo?.broker_company_id || latestCompleted?.broker_company_id || latestScheduled?.broker_company_id || null);
                            const cpMobile = isCase2 
                                ? (leadInfo?.last_tag_cp_user_mobile || leadInfo?.last_tag_cp_mobile || '') 
                                : (leadInfo?.cp_mobile || leadInfo?.broker_mobile || latestCompleted?.cp_user_mobile || latestScheduled?.cp_user_mobile || '');

                            if (setBrokerName) setBrokerName(cpUserName);
                            if (setBrokerMobile) setBrokerMobile(cpMobile);
                            if (setCompanyName) setCompanyName(cpCompanyName);
                            if (setCompanyId) {
                                setCompanyId({
                                    label: cpCompanyName,
                                    value: brokerCompanyId
                                });
                            }

                            // Send CP OTP and proceed to verification page
                            handleSendOTP(true);
                        }
                    });
                }}
            >
                Continue with {cpManagerName}
            </button>
        </>
    ) : isWithCP && hasCompletedVisit && (!!(data?.cp_company_name || data?.cp_user_name)) ? (
        <>
            <button
                className='flex-1 border border-gray-300 rounded-xl py-3 px-4 font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                onClick={() => {
                    setConfirmPopup({
                        show: true,
                        title: 'Are you Sure?',
                        subText: "Do you want to change tagging for this customer?",
                        onConfirm: () => {
                            if (setIsChangeTagging) setIsChangeTagging(true);
                            if (setExistingTagging) setExistingTagging('change');
                            closeModal();
                            if (setIsWithCP) setIsWithCP(true);

                            // Clear any parent CP state
                            if (setBrokerName) setBrokerName('');
                            if (setBrokerMobile) setBrokerMobile('');
                            if (setCompanyName) setCompanyName('');
                            if (setCompanyId) setCompanyId(null);

                            // Redirect to CP selection screen (BrokerDetailForm)
                            if (setChannelPartner) setChannelPartner('4');
                        }
                    });
                }}
            >
                Change Tagging
            </button>
            <button
                className='flex-1 bg-primary text-white rounded-xl py-3 px-4 font-medium hover:opacity-90 transition-opacity'
                onClick={() => {
                    if (setIsChangeTagging) setIsChangeTagging(false);
                    if (setExistingTagging) setExistingTagging('keep');
                    if (setRera) setRera(leadInfo?.rera_number || leadInfo?.rera || latestCompleted?.rera_number || latestCompleted?.rera || latestScheduled?.rera_number || latestScheduled?.rera || '');
                    closeModal();
                    // Ensure check-in type is set to CP visit
                    if (setIsWithCP) setIsWithCP(true);
                    
                    // Populate parent states with existing CP info
                    if (setBrokerName) setBrokerName(data?.cp_user_name || '');
                    if (setBrokerMobile) setBrokerMobile(leadInfo?.cp_mobile || leadInfo?.broker_mobile || latestCompleted?.cp_user_mobile || latestScheduled?.cp_user_mobile || '');
                    if (setCompanyName) setCompanyName(data?.cp_company_name || '');
                    if (setCompanyId) {
                        setCompanyId({
                            label: data?.cp_company_name,
                            value: leadInfo?.broker_company_id || latestCompleted?.broker_company_id || latestScheduled?.broker_company_id || null
                        });
                    }

                    // Send CP OTP and proceed to verification page (No site head OTP required)
                    handleSendOTP(true);
                }}
            >
                Continue with {cpManagerName}
            </button>
        </>
    ) : (
        <>
            {isWithCP && (
                <button
                    className='flex-1 border border-gray-300 rounded-xl py-3 px-4 font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                    onClick={() => {
                        const cpNameLabel = data?.cp_user_name || data?.cp_company_name;
                        const hasExistingCP = !!cpNameLabel;
                        const cpMsg = cpNameLabel ? cpNameLabel : 'CP';

                        let subText = '';
                        if (!hasCompletedVisit) {
                            if (latestScheduled) {
                                subText = "Presale scheduled site visit will be cancelled. Are you sure you want to continue with CP?";
                            } else {
                                subText = "Are you sure you want to continue with CP?";
                            }
                        } else {
                            subText = hasExistingCP 
                                ? `${cpMsg} tagging will be assigned for this customer`
                                : "CP tagging will be changed for this customer. Closing visit will be cancelled";
                        }

                        setConfirmPopup({
                            show: true,
                            title: 'Are you Sure?',
                            subText: subText,
                            onConfirm: () => {
                                if (!hasCompletedVisit) {
                                    if (setIsChangeTagging) setIsChangeTagging(true);
                                    if (setExistingTagging) setExistingTagging('change');
                                    if (setIsCancelInternalVisit) setIsCancelInternalVisit(!!latestScheduled);
                                    closeModal();
                                    // Ensure check-in type is set to CP visit
                                    if (setIsWithCP) setIsWithCP(true);

                                    // Clear any parent CP state
                                    if (setBrokerName) setBrokerName('');
                                    if (setBrokerMobile) setBrokerMobile('');
                                    if (setCompanyName) setCompanyName('');
                                    if (setCompanyId) setCompanyId(null);

                                    // Redirect to CustomerDetails page to fill basic details
                                    if (setChannelPartner) setChannelPartner('3');
                                } else {
                                    const hasExistingCP = !!(data?.cp_company_name || data?.cp_user_name);
                                    
                                    if (hasExistingCP) {
                                        if (setIsChangeTagging) setIsChangeTagging(false);
                                        if (setExistingTagging) setExistingTagging('keep');
                                        if (setRera) setRera(leadInfo?.rera_number || leadInfo?.rera || latestCompleted?.rera_number || latestCompleted?.rera || latestScheduled?.rera_number || latestScheduled?.rera || '');
                                        closeModal();
                                        // Ensure check-in type is set to CP visit
                                        if (setIsWithCP) setIsWithCP(true);
                                        
                                        // Populate parent states with existing CP info
                                        if (setBrokerName) setBrokerName(data?.cp_user_name || '');
                                        if (setBrokerMobile) setBrokerMobile(leadInfo?.cp_mobile || leadInfo?.broker_mobile || latestCompleted?.cp_user_mobile || latestScheduled?.cp_user_mobile || '');
                                        if (setCompanyName) setCompanyName(data?.cp_company_name || '');
                                        if (setCompanyId) {
                                            setCompanyId({
                                                label: data?.cp_company_name,
                                                value: leadInfo?.broker_company_id || latestCompleted?.broker_company_id || latestScheduled?.broker_company_id || null
                                            });
                                        }

                                        // Send CP OTP and proceed to verification page
                                        handleSendOTP(true);
                                    } else {
                                        if (setIsChangeTagging) setIsChangeTagging(true);
                                        if (setExistingTagging) setExistingTagging('change');
                                        closeModal();
                                        // Ensure check-in type is set to CP visit
                                        if (setIsWithCP) setIsWithCP(true);

                                        // Clear any parent CP state
                                        if (setBrokerName) setBrokerName('');
                                        if (setBrokerMobile) setBrokerMobile('');
                                        if (setCompanyName) setCompanyName('');
                                        if (setCompanyId) setCompanyId(null);

                                        // Redirect to CP selection screen (BrokerDetailForm)
                                        if (setChannelPartner) setChannelPartner('4');
                                    }
                                }
                            }
                        });
                    }}
                >
                    Continue Visit with CP
                </button>
            )}

            {/* Button 2 (Primary style): Continue with Direct Team / Continue Revisit */}
            <button
                className='flex-1 bg-primary text-white rounded-xl py-3 px-4 font-medium hover:opacity-90 transition-opacity'
                onClick={() => {
                    if (!hasCompletedVisit) {
                        if (setIsChangeTagging) setIsChangeTagging(false);
                        if (setExistingTagging) setExistingTagging('keep');
                        if (setIsCancelInternalVisit) setIsCancelInternalVisit(false);
                        closeModal();
                        
                        // Clear CP info in parent
                        if (setCompanyId) setCompanyId(null);
                        if (setBrokerName) setBrokerName('');
                        if (setBrokerMobile) setBrokerMobile('');
                        if (setCompanyName) setCompanyName('');
                        if (setIsWithCP) setIsWithCP(false);

                        // Send Direct OTP and proceed directly to OTP page
                        handleSendOTP(false);
                    } else {
                        if (isWithCP) {
                            const cpNameLabel = data?.cp_user_name || data?.cp_company_name;
                            const hasExistingCP = !!cpNameLabel;

                            if (hasExistingCP) {
                                setConfirmPopup({
                                    show: true,
                                    title: 'Are you Sure?',
                                    subText: "CP tagging will be changed for this customer. Are you sure you want to continue with Direct?",
                                    onConfirm: () => {
                                        if (setIsChangeTagging) setIsChangeTagging(true);
                                        if (setExistingTagging) setExistingTagging('change');
                                        closeModal();
                                        
                                        // Clear CP info in parent
                                        if (setCompanyId) setCompanyId(null);
                                        if (setBrokerName) setBrokerName('');
                                        if (setBrokerMobile) setBrokerMobile('');
                                        if (setCompanyName) setCompanyName('');
                                        if (setIsWithCP) setIsWithCP(false);

                                        // Send Direct OTP and proceed
                                        handleSendOTP(false);
                                    }
                                });
                            } else {
                                // If already Direct (no existing CP), bypass the popup and proceed directly
                                if (setIsChangeTagging) setIsChangeTagging(false);
                                if (setExistingTagging) setExistingTagging('change');
                                closeModal();
                                
                                // Clear CP info in parent
                                if (setCompanyId) setCompanyId(null);
                                if (setBrokerName) setBrokerName('');
                                if (setBrokerMobile) setBrokerMobile('');
                                if (setCompanyName) setCompanyName('');
                                if (setIsWithCP) setIsWithCP(false);

                                // Send Direct OTP and proceed
                                handleSendOTP(false);
                            }
                        } else {
                            // Standard Direct Revisit Flow (No CP involved)
                            if (setIsChangeTagging) setIsChangeTagging(false);
                            if (setExistingTagging) setExistingTagging('keep');
                            closeModal();
                            handleSendOTP(false);
                        }
                    }
                }}
            >
                {isWithCP ? "Continue with Direct Team" : "Continue Revisit"}
            </button>
        </>
    )}
</div>
                <AreYouSureModal
                    show={confirmPopup.show}
                    closeModal={() => setConfirmPopup(prev => ({ ...prev, show: false }))}
                    confirmClose={() => {
                        if (confirmPopup.onConfirm) confirmPopup.onConfirm();
                        setConfirmPopup(prev => ({ ...prev, show: false }));
                    }}
                    title={confirmPopup.title}
                    subText={confirmPopup.subText}
                    buttonTitle="Confirm"
                    dialogClassName="unprocessed-leads PauseIntegrationModal DeleteIntegrationModal revisit-confirm-modal"
                    backdropClassName="revisit-confirm-backdrop"
                />
            </div>
        </CenterModal >
    )
}

export default CustomerRevisitModal